import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAccess } from "./utils/AccessContext";
import "./dSpr.css";
import "./Leave_approval_Levels.css";

// Sort presets for the filter row. "manual" keeps the saved display_order
// (drag-and-drop). The rest are read-only view sorts - drag is disabled
// while any non-manual sort is active so a drop can't overwrite the intent.
const LAL_SORT_OPTIONS = [
    { value: "manual",   label: "Manual order" },
    { value: "name_asc", label: "Department A → Z" },
    { value: "name_desc",label: "Department Z → A" },
    { value: "has_emp",  label: "Has employees first" },
    { value: "no_emp",   label: "No employees first" },
];

function Leave_approval_Levels({ navSize }) {
    // hasAccess reads the live Access Center matrix - no hardcoded role list.
    // Toggle "dTime > Leave Approval Levels" in Access_level_configuration
    // to control write actions for any role.
    const { hasAccess, matrix } = useAccess();
    const canManageApprovalLevels = hasAccess("dTime", "Leave Approval Levels");

    // Roles that currently have "dTime > Leave Approvals" ON in Access Center.
    // Only these roles can approve/reject, so only they should be selectable
    // as Level 1/2/3 approvers - assigning a User-tier employee would create
    // a request they can never act on.
    const APPROVER_ROLES = useMemo(() => {
        const row = matrix.find(r => r.category === "dTime" && r.page_name === "Leave Approvals");
        if (!row) return new Set();
        const roles = new Set();
        if (row.admin_access    === 1) roles.add("Admin");
        if (row.subadmin_access === 1) roles.add("Sub Admin");
        if (row.manager_access  === 1) roles.add("Manager");
        if (row.user_access     === 1) roles.add("User");
        return roles;
    }, [matrix]);

    const [departments, setDepartments] = useState([]);
    const [approvalData, setApprovalData] = useState([]);
    const [editingDept, setEditingDept] = useState(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkDirty, setBulkDirty] = useState(new Set());   // dept_ids changed in bulk mode
    const [bulkSaving, setBulkSaving] = useState(false);
    const [departmentList, setDepartmentList] = useState([]);
    const [loggedInEmp, setLoggedInEmp] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [sortOrder, setSortOrder] = useState("manual");
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const navigate = useNavigate();

    // Close sort dropdown when clicking outside
    useEffect(() => {
        const onDocClick = (e) => {
            if (!e.target.closest("[data-lal-sort-dropdown]")) setShowSortDropdown(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        fetchDepartments();
        fetchloginedEmployees();
        fetchEmployees();
        fetchApprovals();
    }, []);

    // 🔹 Load existing leave_approval rows so Level 1/2/3 dropdowns
    //    pre-fill on page load / after refresh.
    //    Backend returns department_id as a STRING ("6") but everything else
    //    on this page compares against dept.department_id as a NUMBER, so
    //    coerce here - otherwise every find() misses and dropdowns stay blank.
    const fetchApprovals = async () => {
        try {
            const res = await apiFetch("/api/leave/leave_approval_list", {
                method: "GET",
                credentials: "include",
            });
            const data = await res.json();
            if (!Array.isArray(data)) return;
            setApprovalData(data.map(r => ({
                department: Number(r.department_id),
                level1: r.level_1 || "",
                level2: r.level_2 || "",
                level3: r.level_3 || "",
            })));
        } catch (err) {
            console.error("❌ Approvals fetch error:", err);
        }
    };

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
             
            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            if (data[0].emp_id) {
                setLoggedInEmp(data[0]);
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            }

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const fetchEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/all`);
            const data = await res.json();
            setEmployees(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    // 🔹 Fetch department order for logged-in employee
    const fetchDepartments = async () => {
        const res = await apiFetch(`/api/Employee_configuration/get-department-order`, {
            method: 'GET',
            credentials: 'include',
        });
        const data = await res.json();
        setDepartmentList(data.departments || []);
    };

    // 🔹 Save reordered department list
    const saveDepartmentOrder = async (newList) => {
        const orderData = newList.map((d, index) => ({
            department_id: d.department_id,
            display_order: index + 1,
        }));

        try {
            const res = await apiFetch("/api/Employee_configuration/save-department-order", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ order: orderData }),
                credentials: 'include',
            });

            const data = await res.json();
            if (data.success) {
            } else {
                dnAlert.error("Error", data.error || "Failed to save order");
            }
        } catch (err) {
            console.error("❌ Save order error:", err);
        }
    };

    // 🔹 Handle drag end - only allowed in Manual sort mode so a drop
    //    can't overwrite the saved order while a view-sort is active.
    const handleDragEnd = (result) => {
        if (!result.destination) return;
        if (sortOrder !== "manual") return;
        const newList = Array.from(departmentList);
        const [moved] = newList.splice(result.source.index, 1);
        newList.splice(result.destination.index, 0, moved);
        setDepartmentList(newList);
        saveDepartmentOrder(newList);
    };

    // 🔹 Handle value change
    const handleChange = (deptId, level, empId) => {
        setApprovalData((prev) => {
            const updated = [...prev];
            const index = updated.findIndex((d) => d.department === deptId);
            if (index > -1) {
                updated[index][level] = empId;
            } else {
                updated.push({
                    department: deptId,
                    level1: level === "level1" ? empId : "",
                    level2: level === "level2" ? empId : "",
                    level3: level === "level3" ? empId : "",
                });
            }
            return updated;
        });
        // In bulk mode every touched dept is queued for the next Save All.
        if (bulkMode) {
            setBulkDirty((prev) => {
                const next = new Set(prev);
                next.add(deptId);
                return next;
            });
        }
    };

    // 🔹 Bulk: save every dept the admin touched while in bulk mode.
    //   Validates all-3-levels-set per row first; reports per-row outcome.
    const handleBulkSave = async () => {
        const targets = Array.from(bulkDirty).map(id =>
            approvalData.find(d => d.department === id)
        ).filter(Boolean);
        if (targets.length === 0) {
            dnAlert.warning("Nothing to save", "No rows were changed.");
            return;
        }
        const incomplete = targets.filter(d => !d.level1 || !d.level2 || !d.level3);
        if (incomplete.length > 0) {
            dnAlert.error(
                "Validation Error",
                `${incomplete.length} row${incomplete.length === 1 ? "" : "s"} missing Level 1/2/3 - fill all three before saving.`
            );
            return;
        }
        setBulkSaving(true);
        const results = await Promise.all(targets.map(d =>
            apiFetch("/api/leave/save_leave_approval", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    department_id: d.department,
                    level_1: d.level1,
                    level_2: d.level2,
                    level_3: d.level3,
                }),
                credentials: 'include',
            }).then(r => r.json()).then(j => ({ ok: !!j.success, id: d.department }))
              .catch(() => ({ ok: false, id: d.department }))
        ));
        setBulkSaving(false);
        const ok = results.filter(r => r.ok).length;
        const fail = results.length - ok;
        if (fail === 0) {
            dnAlert.success("Saved", `${ok} department${ok === 1 ? "" : "s"} updated.`);
            setBulkDirty(new Set());
            setBulkMode(false);
        } else {
            dnAlert.error("Partial save", `${ok} saved, ${fail} failed. Failed rows remain editable.`);
            // Keep only the failed ones marked dirty so the user can retry.
            setBulkDirty(new Set(results.filter(r => !r.ok).map(r => r.id)));
        }
    };

    const handleBulkCancel = () => {
        // approvalData is never re-fetched from backend (page only tracks
        // new values), so cancel just exits bulk mode and clears the
        // dirty tracker. Any in-flight changes stay in state but won't
        // be auto-submitted because the dirty set is empty.
        setBulkDirty(new Set());
        setBulkMode(false);
    };

    // 🔹 Handle Edit / Save
    const handleEditSave = async (deptId) => {
        if (editingDept === deptId) {
            const data = approvalData.find((d) => d.department === deptId);
            if (!data?.level1 || !data?.level2 || !data?.level3) {
                dnAlert.error("Validation Error", "Please select all levels");
                return;
            }

            try {
                const response = await apiFetch("/api/leave/save_leave_approval", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        department_id: data.department,
                        level_1: data.level1,
                        level_2: data.level2,
                        level_3: data.level3,
                    }),
                    credentials: 'include',
                });

                const result = await response.json();

                if (result.success) {
                    dnAlert.success("Success", "Approval levels saved successfully");
                } else {
                    dnAlert.error("Error", result.message || "Something went wrong");
                }
            } catch (error) {
                console.error("❌ Save error:", error);
                dnAlert.error("Error", "Failed to save approval levels");
            }

            setEditingDept(null);
        } else {
            setEditingDept(deptId);
        }
    };

    // 🔹 Filter employees per department AND by role - only roles that have
    //    "dTime > Leave Approvals" access can act on requests, so only those
    //    should show up as eligible approvers.
    const getEmployeesByDept = (deptId) => {
        const dept = departmentList.find((d) => d.department_id === deptId);
        return employees.filter((e) =>
            e.emp_department === dept?.department_name &&
            APPROVER_ROLES.has(e.emp_access_level)
        );
    };

    // 🔹 Apply the sort preset to a base list (view-only reorder; the saved
    //    display_order is untouched unless the user drags in Manual Order).
    const applySort = (list) => {
        if (sortOrder === "manual") return list;
        const copy = [...list];
        const empCount = (d) => employees.filter(e => e.emp_department === d.department_name).length;
        switch (sortOrder) {
            case "name_asc":  return copy.sort((a, b) => a.department_name.localeCompare(b.department_name));
            case "name_desc": return copy.sort((a, b) => b.department_name.localeCompare(a.department_name));
            case "has_emp":   return copy.sort((a, b) => empCount(b) - empCount(a) || a.department_name.localeCompare(b.department_name));
            case "no_emp":    return copy.sort((a, b) => empCount(a) - empCount(b) || a.department_name.localeCompare(b.department_name));
            default: return list;
        }
    };

    return (
        <div className="leave-config-container">
            <div className={`leave-config-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img
                                src={logo_eagle}
                                alt="dAdmin Logo"
                                style={{
                                    height: "50px",
                                    objectFit: "contain",
                                    marginLeft: "-20px",
                                }}
                            />
                        </a>
                        Leave Approval Levels
                    </h2>
                    <p className="dspr-subtitle">Set the 3-tier approval chain per department. Each level routes to the named employee in order.</p>
                </div>

                {/* 🔹 Department Filter + Bulk-edit toolbar (right-aligned) */}
                <div className="leave-approval-filter-section">
                    <label style={{ fontWeight: 600, color: "#374151" }}>
                        Filter by Department:
                    </label>
                    <Select
                        placeholder="Select department(s)"
                        options={departmentList.map((dept) => ({
                            value: dept.department_id,
                            label: dept.department_name,
                        }))}
                        value={
                            departments.length
                                ? departments.map((id) => {
                                    const d = departmentList.find((dep) => dep.department_id === id);
                                    return { value: d?.department_id, label: d?.department_name };
                                })
                                : null
                        }
                        onChange={(selected) => {
                            const selectedValues = selected ? selected.map((s) => s.value) : [];
                            setDepartments(selectedValues);
                        }}
                        isMulti
                        {...checkboxMultiProps}
                        isClearable
                        isSearchable
                        closeMenuOnSelect={false}
                    />

                    {/* Sort dropdown - view-only reorder, drag stays enabled
                        only in Manual Order. */}
                    <div className="lal-sort-slot" data-lal-sort-dropdown>
                        <button
                            className={`lal-sort-btn ${showSortDropdown ? "open" : ""}`}
                            onClick={() => setShowSortDropdown(p => !p)}
                            title="Sort departments"
                        >
                            <span style={{ fontSize: 15 }}>&#8645;</span>
                            {LAL_SORT_OPTIONS.find(o => o.value === sortOrder)?.label || "Sort by…"}
                            <span style={{ fontSize: 10, marginLeft: 2 }}>▼</span>
                        </button>
                        {showSortDropdown && (
                            <div className="lal-sort-menu">
                                <div className="lal-sort-menu-header">&#8645; Sort by…</div>
                                {LAL_SORT_OPTIONS.map(opt => (
                                    <div
                                        key={opt.value}
                                        className={`lal-sort-menu-item ${sortOrder === opt.value ? "active" : ""}`}
                                        onClick={() => { setSortOrder(opt.value); setShowSortDropdown(false); }}
                                    >
                                        {opt.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Bulk-edit controls follow the Access Center matrix
                        so admins can grant / revoke without a code change. */}
                    {canManageApprovalLevels && (
                        <div className="lal-bulk-slot">
                            {!bulkMode ? (
                                <button
                                    className="lal-bulk-enter"
                                    onClick={() => setBulkMode(true)}
                                    title="Edit every department's levels in one pass"
                                >
                                    Bulk Edit
                                </button>
                            ) : (
                                <>
                                    <span className="lal-bulk-info">
                                        <strong>{bulkDirty.size}</strong> pending
                                    </span>
                                    <button
                                        className="lal-bulk-cancel"
                                        onClick={handleBulkCancel}
                                        disabled={bulkSaving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="lal-bulk-save"
                                        onClick={handleBulkSave}
                                        disabled={bulkSaving || bulkDirty.size === 0}
                                    >
                                        {bulkSaving
                                            ? "Saving…"
                                            : `Save All${bulkDirty.size ? ` (${bulkDirty.size})` : ""}`}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="leave-config-content">
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="departments">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {applySort(
                                        departments.length
                                            ? departmentList.filter((d) => departments.includes(d.department_id))
                                            : departmentList
                                    ).map((dept, index) => {
                                        const deptEmployees = getEmployeesByDept(dept.department_id);
                                        const deptData = approvalData.find((d) => d.department === dept.department_id) || {};

                                        return (
                                            <Draggable
                                                key={dept.department_id.toString()}
                                                draggableId={dept.department_id.toString()}
                                                index={index}
                                            >
                                                {(provided) => (
                                                    <div
                                                        className="approval-row"
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            ...provided.draggableProps.style,
                                                            background: "#fff",
                                                            marginBottom: "10px",
                                                            borderRadius: "8px",
                                                            padding: "10px",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                        }}
                                                    >
                                                        {/* Department Name */}
                                                        <div className="approval-group">
                                                            <label>Department:</label>
                                                            <input
                                                                type="text"
                                                                value={dept.department_name}
                                                                readOnly
                                                                className="dept-input"
                                                                title={dept.department_name}
                                                            />
                                                        </div>

                                                        {/* Approval Levels */}
                                                        {["level1", "level2", "level3"].map((level, i) => (
                                                            <div className="approval-group" key={i}>
                                                                <label>Level {i + 1}:</label>
                                                                <select
                                                                    value={deptData[level] || ""}
                                                                    disabled={
                                                                        deptEmployees.length === 0 ||
                                                                        (!bulkMode && editingDept !== dept.department_id)
                                                                    }
                                                                    onChange={(e) => handleChange(dept.department_id, level, e.target.value)}
                                                                >
                                                                    <option value="">Select</option>
                                                                    {deptEmployees.map((emp) => (
                                                                        <option key={emp.emp_id} value={emp.emp_id}>
                                                                            {emp.emp_first_name} {emp.emp_last_name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        ))}

                                                        {/* Edit/Save Button - hidden in bulk mode (the page-level
                                                            Save All replaces it). Follows Access Center matrix. */}
                                                        {canManageApprovalLevels && !bulkMode && (
                                                            <button
                                                                className="btn-edit"
                                                                onClick={() => handleEditSave(dept.department_id)}
                                                                disabled={deptEmployees.length === 0}
                                                                style={{
                                                                    opacity: deptEmployees.length === 0 ? 0.5 : 1,
                                                                    cursor: deptEmployees.length === 0 ? "not-allowed" : "pointer",
                                                                }}
                                                            >
                                                                {editingDept === dept.department_id ? "Save" : "Edit"}
                                                            </button>
                                                        )}
                                                        {/* Dirty-row marker in bulk mode */}
                                                        {bulkMode && bulkDirty.has(dept.department_id) && (
                                                            <span className="bulk-dirty-pill" title="Unsaved change">●</span>
                                                        )}

                                                        {deptEmployees.length === 0 && (
                                                            <div
                                                                style={{
                                                                    gridColumn: "span 3",
                                                                    color: "#b91c1c",
                                                                    fontWeight: 600,
                                                                    fontSize: "14px",
                                                                }}
                                                            >
                                                                ⚠️ No employees found for this department.
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>

            </div>
        </div >
    );
}

export default Leave_approval_Levels;
